import i18n from '../../../i18n';

/**
 * Every number, percentage, date and amount Growth shows, in the person's
 * language (WP4.1): Swiss German (de-CH) or British English (en-GB). Nothing
 * in Growth formats with a hard-coded locale; units and plurals come from the
 * Growth catalog (`growth:format.*`).
 */
export type GrowthLocale = 'de-CH' | 'en-GB';

export const DASH = '—';
const MINUS = '−';

export function growthLocale(): GrowthLocale {
  return i18n.language?.startsWith('de') ? 'de-CH' : 'en-GB';
}

const numberFormats = new Map<string, Intl.NumberFormat>();
const dateFormats = new Map<string, Intl.DateTimeFormat>();

function numberFormat(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const locale = growthLocale();
  const key = `${locale}|${JSON.stringify(options)}`;
  let format = numberFormats.get(key);
  if (!format) {
    format = new Intl.NumberFormat(locale, options);
    numberFormats.set(key, format);
  }
  return format;
}

function dateFormat(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const locale = growthLocale();
  const key = `${locale}|${JSON.stringify(options)}`;
  let format = dateFormats.get(key);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, options);
    dateFormats.set(key, format);
  }
  return format;
}

function finite(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value);
}

function t(key: string, options?: Record<string, unknown>): string {
  return i18n.t(`growth:format.${key}`, options);
}

/** A whole number with the locale's grouping: 1,234 or 1’234. */
export function fmtInt(value: number | null | undefined): string {
  return finite(value) ? numberFormat({ maximumFractionDigits: 0 }).format(Math.round(value)) : DASH;
}

/** A number with a fixed number of decimals. */
export function fmtNumber(value: number | null | undefined, digits = 0): string {
  if (!finite(value)) return DASH;
  return numberFormat({ minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);
}

/** A share (0.123) as a percentage: 12% or 12.3%. */
export function fmtShare(value: number | null | undefined, digits = 0): string {
  if (!finite(value)) return DASH;
  return numberFormat({
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/**
 * A small conversion rate that stays legible: one decimal from 1%, two below,
 * none from 10%. One decimal would turn 0.04% into "0.0%".
 */
export function fmtRate(value: number | null | undefined): string {
  if (!finite(value)) return DASH;
  const pct = Math.abs(value * 100);
  return fmtShare(value, pct >= 10 ? 0 : pct >= 1 ? 1 : 2);
}

/** A difference between two rates in percentage points: "1.2 pt". */
export function fmtPoints(points: number, digits = 1): string {
  return t('points', { value: fmtNumber(points, digits) });
}

/** "+12" or "−3", with a real minus sign. */
export function fmtSigned(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? MINUS : '';
  return `${sign}${fmtInt(Math.abs(value))}`;
}

/** "+12%" or "−3%" for a relative change. */
export function fmtSignedShare(value: number, digits = 0): string {
  const sign = value > 0 ? '+' : value < 0 ? MINUS : '';
  return `${sign}${fmtShare(Math.abs(value), digits)}`;
}

/** An amount in the venue's currency, whole units. */
export function fmtMoney(value: number | null | undefined, currency: string | null | undefined): string {
  if (!finite(value)) return DASH;
  if (!currency) return fmtInt(value);
  try {
    return numberFormat({ style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
  } catch {
    return `${currency} ${fmtInt(value)}`;
  }
}

/** Scorecard money: whole units below 10,000, then compact so the figure fits a tile. */
export function fmtMoneyShort(value: number | null | undefined, currency: string | null | undefined): string {
  if (!finite(value) || Math.abs(value) < 10_000) return fmtMoney(value, currency);
  const compact = numberFormat({
    notation: 'compact',
    maximumFractionDigits: Math.abs(value) < 1_000_000 ? 0 : 1,
  }).format(value);
  return currency ? `${currency} ${compact}` : compact;
}

/** A lead time in days: "3.5 d", "12 d". */
export function fmtDays(value: number | null | undefined): string {
  if (!finite(value)) return DASH;
  const shown = value < 10 ? fmtNumber(value, 1) : fmtInt(value);
  return t('daysShort', { value: shown });
}

/** A count with its plural unit from the catalog, e.g. "1 day", "4 days". */
export function fmtCount(key: string, count: number): string {
  return t(key, { count, value: fmtInt(count) });
}

/** How long something took: "5 min", "3 h", "4 days". */
export function fmtDuration(seconds: number | null | undefined): string {
  if (!finite(seconds)) return DASH;
  const minutes = seconds / 60;
  if (minutes < 60) return t('minutes', { value: fmtInt(Math.max(1, Math.round(minutes))) });
  const hours = minutes / 60;
  if (hours < 48) return t('hours', { value: fmtInt(Math.round(hours)) });
  return fmtCount('days', Math.round(hours / 24));
}

/** Data-driven credit is fractional; whole numbers stay whole. */
export function fmtCredit(value: number | null | undefined): string {
  if (!finite(value)) return DASH;
  return Number.isInteger(value) ? fmtInt(value) : fmtNumber(value, 1);
}

function isoDate(iso: string): Date | null {
  const date = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** "12 Aug" or "12. Aug." for a YYYY-MM-DD day. */
export function fmtShortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = isoDate(iso);
  if (!date) return iso;
  return dateFormat({ day: 'numeric', month: 'short', timeZone: 'UTC' }).format(date);
}

/** "12 Aug 2026" or "12.08.2026" for a YYYY-MM-DD day. */
export function fmtLongDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = isoDate(iso);
  if (!date) return iso;
  return dateFormat({ dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

/**
 * A span of days, as short as the language writes it: "14 Sept – 11 Oct
 * 2026" or "14. Sept. – 11. Okt. 2026" (YYYY-MM-DD in, inclusive).
 */
export function fmtDateRange(start: string | null | undefined, end: string | null | undefined): string {
  if (!start || !end) return fmtLongDate(start || end);
  const from = isoDate(start);
  const to = isoDate(end);
  if (!from || !to) return `${start} – ${end}`;
  // formatRange is in every supported browser, not yet in this TS lib.
  const format = dateFormat({ day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) as Intl.DateTimeFormat & {
    formatRange?: (from: Date, to: Date) => string;
  };
  if (from.getTime() === to.getTime()) return format.format(from);
  return typeof format.formatRange === 'function'
    ? format.formatRange(from, to)
    : `${format.format(from)} – ${format.format(to)}`;
}

/** "Aug 2026" for the month a day falls in. */
export function fmtMonthYear(iso: string): string {
  const date = isoDate(iso);
  if (!date) return iso;
  return dateFormat({ month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date);
}

/** "Aug 26" for a cohort month written YYYY-MM. */
export function fmtCohortMonth(cohort: string): string {
  const [year, month] = cohort.split('-').map(Number);
  if (!year || !month) return cohort;
  return dateFormat({ month: 'short', year: '2-digit', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, 1)),
  );
}

/** The parts of a day chip: "Sat", "12", "Aug" and "Saturday". */
export function fmtDayChip(iso: string): { weekday: string; day: string; month: string; long: string } {
  const date = isoDate(iso) ?? new Date();
  return {
    weekday: dateFormat({ weekday: 'short', timeZone: 'UTC' }).format(date),
    day: dateFormat({ day: 'numeric', timeZone: 'UTC' }).format(date),
    month: dateFormat({ month: 'short', timeZone: 'UTC' }).format(date),
    long: dateFormat({ weekday: 'long', timeZone: 'UTC' }).format(date),
  };
}

/** "14:05" today, "12 Oct, 14:05" before. */
export function fmtFetchTime(ms: number | null | undefined, now = new Date()): string {
  if (!ms) return '';
  const when = new Date(ms);
  if (Number.isNaN(when.getTime())) return '';
  const time = dateFormat({ hour: '2-digit', minute: '2-digit' }).format(when);
  if (when.toDateString() === now.toDateString()) return time;
  return `${dateFormat({ day: 'numeric', month: 'short' }).format(when)}, ${time}`;
}

/** "12 Oct 2026, 14:05": a moment, for staff lists. */
export function fmtDateTime(ms: number | null | undefined): string {
  if (!ms) return '';
  const when = new Date(ms);
  if (Number.isNaN(when.getTime())) return '';
  return dateFormat({ dateStyle: 'medium', timeStyle: 'short' }).format(when);
}

/** "12 Oct 2026": a day, for staff lists. */
export function fmtDate(ms: number | null | undefined): string {
  if (!ms) return '';
  const when = new Date(ms);
  if (Number.isNaN(when.getTime())) return '';
  return dateFormat({ dateStyle: 'medium' }).format(when);
}

/** "14:05". */
export function fmtTime(ms: number | null | undefined): string {
  if (!ms) return '';
  const when = new Date(ms);
  if (Number.isNaN(when.getTime())) return '';
  return dateFormat({ timeStyle: 'short' }).format(when);
}

/** A list in the person's language: "A, B and C" or "A, B und C". */
export function fmtList(items: string[]): string {
  if (items.length < 2) return items.join('');
  return t('list', { rest: items.slice(0, -1).join(', '), last: items[items.length - 1] });
}
